import { KERNEL_AI_FUNCTIONS, type AiFunctionDeclaration } from './ai';
import { LOCALES, DEFAULT_LOCALE } from './i18n';
import type { DataProcessingDeclaration, DefaultRole, ModuleManifest } from './manifest';
import { modulePermissions, moduleSettings } from './manifest';
import type { CatalogPermission } from './permissions';
import type { CatalogSetting } from './settings';

/**
 * What the kernel itself declares, in the same shape modules use. `platform.*` permissions sit
 * above the companies (ADR-0009), `company.*` permissions cover the administration of the active
 * company, `core.*` holds kernel settings and what every employee uses, like the people directory.
 */
export const KERNEL_PERMISSIONS: readonly CatalogPermission[] = [
  { key: 'platform.superadmin', owner: 'platform', level: 'platform', privileged: true },
  { key: 'platform.companies.manage', owner: 'platform', level: 'platform', privileged: true },
  { key: 'platform.modules.manage', owner: 'platform', level: 'platform', privileged: true },
  { key: 'platform.audit.read', owner: 'platform', level: 'platform', privileged: true },
  { key: 'platform.metrics.read', owner: 'platform', level: 'platform' },
  { key: 'platform.backups.create', owner: 'platform', level: 'platform', privileged: true },
  { key: 'company.accounts.read', owner: 'company', level: 'company' },
  { key: 'company.accounts.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.groups.read', owner: 'company', level: 'company' },
  { key: 'company.groups.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.roles.read', owner: 'company', level: 'company' },
  { key: 'company.roles.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.audit.read', owner: 'company', level: 'company', privileged: true },
  { key: 'company.settings.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.branding.manage', owner: 'company', level: 'company' },
  { key: 'company.sso.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.notifications.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.custom-fields.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.shares.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.privacy.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.ai.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.integrations.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.findings.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'company.modules.manage', owner: 'company', level: 'company', privileged: true },
  { key: 'core.people.read', owner: 'core', level: 'company' },
];

/**
 * Actions the kernel writes to the audit logs. The admin UI names each of them in both languages,
 * the server tests check that nothing outside this list is written.
 */
export const KERNEL_AUDIT_ACTIONS = [
  'account.notifications.push-off',
  'account.notifications.push-on',
  'auth.access',
  'auth.company-switch',
  'auth.login',
  'auth.logout',
  'auth.passkey.add',
  'auth.passkey.remove',
  'auth.password.change',
  'auth.password.reset',
  'auth.two-factor.backup-codes',
  'auth.two-factor.disable',
  'auth.two-factor.enable',
  'company.accounts.create',
  'company.accounts.invite',
  'company.accounts.link',
  'company.ai.test',
  'company.ai.update',
  'company.accounts.lock',
  'company.accounts.password-reset',
  'company.accounts.provision',
  'company.accounts.two-factor-reset',
  'company.accounts.unlink',
  'company.accounts.unlock',
  'company.audit.read',
  'company.branding.logo',
  'company.branding.logo-remove',
  'company.branding.update',
  'company.custom-fields.create',
  'company.custom-fields.delete',
  'company.custom-fields.reorder',
  'company.custom-fields.update',
  'company.groups.add-member',
  'company.modules.activation',
  'company.modules.order',
  'company.groups.create',
  'company.groups.delete',
  'company.groups.remove-member',
  'company.groups.update',
  'company.integrations.test',
  'company.integrations.update',
  'company.roles.assign',
  'company.roles.create',
  'company.roles.delete',
  'company.roles.unassign',
  'company.roles.update',
  'company.notifications.test',
  'company.notifications.update',
  'company.privacy.erase',
  'company.privacy.export',
  'company.privacy.retention-update',
  'company.settings.update',
  'company.sso.disable',
  'company.sso.password-login',
  'company.sso.update',
  'company.sso.verify',
  'core.activity.read',
  'core.catalogs.create',
  'core.catalogs.delete',
  'core.catalogs.import',
  'core.catalogs.reorder',
  'core.catalogs.update',
  'core.comments.add',
  'core.comments.remove',
  'core.comments.update',
  'core.custom-fields.read',
  'core.custom-fields.update',
  'core.documents.add',
  'core.documents.folder',
  'core.documents.link',
  'core.documents.remove',
  'core.documents.unlink',
  'core.files.add',
  'core.files.download',
  'core.files.download-zip',
  'core.files.reject',
  'core.files.remove',
  'core.findings.add',
  'core.findings.copy',
  'core.findings.remove',
  'core.findings.update',
  'core.imports.apply',
  'core.plans.add',
  'core.plans.remove',
  'core.plans.update',
  'core.sequences.update',
  'core.privacy.retention',
  'core.records.merge',
  'core.reminders.add',
  'core.reminders.remove',
  'core.reminders.update',
  'core.shares.create',
  'core.shares.incoming-create',
  'core.shares.incoming-revoke',
  'core.shares.incoming-update',
  'core.shares.revoke',
  'core.shares.update',
  'core.signatures.read',
  'core.signatures.remove',
  'core.signatures.update',
  'core.sync.reject',
  'core.tasks.add',
  'core.tasks.remove',
  'core.tasks.update',
  'core.templates.create',
  'core.templates.delete',
  'core.templates.update',
  'core.views.create',
  'core.views.delete',
  'core.views.update',
  'core.workflows.transition',
  'platform.audit.read',
  'platform.backups.create',
  'platform.companies.create',
  'platform.companies.update',
  'platform.grants.create',
  'platform.grants.delete',
  'platform.modules.activation',
  'platform.settings.update',
] as const;

export const KERNEL_SETTINGS: readonly CatalogSetting[] = [
  {
    owner: 'core',
    key: 'core.locale',
    level: 'user',
    placement: 'profile',
    section: 'general',
    type: 'select',
    options: LOCALES,
    default: DEFAULT_LOCALE,
  },
  {
    owner: 'core',
    key: 'core.startPage',
    level: 'user',
    placement: 'profile',
    section: 'general',
    type: 'navigation-target',
    default: '/',
  },
  {
    owner: 'core',
    key: 'core.theme',
    level: 'user',
    placement: 'profile',
    section: 'appearance',
    type: 'select',
    options: ['system', 'light', 'dark'],
    default: 'system',
  },
  {
    owner: 'core',
    key: 'core.density',
    level: 'user',
    placement: 'profile',
    section: 'appearance',
    type: 'select',
    options: ['comfortable', 'compact'],
    default: 'comfortable',
  },
  {
    owner: 'core',
    key: 'core.emailNotifications',
    level: 'user',
    placement: 'profile',
    section: 'notifications',
    type: 'boolean',
    default: true,
  },
  {
    // Invitations for appointments of modules by e-mail (docs/architecture/calendar-invites.md).
    owner: 'core',
    key: 'core.calendarInvites',
    level: 'user',
    placement: 'profile',
    section: 'notifications',
    type: 'boolean',
    default: true,
  },
  {
    owner: 'core',
    key: 'core.teamsNotifications',
    level: 'user',
    placement: 'profile',
    section: 'notifications',
    type: 'boolean',
    default: false,
  },
  {
    owner: 'core',
    key: 'core.motion',
    level: 'user',
    placement: 'profile',
    section: 'accessibility',
    type: 'select',
    options: ['system', 'reduced'],
    default: 'system',
  },
  {
    // Order and hidden tiles of the start page, edited there (docs/architecture/dashboard.md).
    owner: 'core',
    key: 'core.dashboard',
    level: 'user',
    type: 'arrangement',
    default: { order: [], hidden: [] },
  },
  {
    // Order of the modules in the sidebar and on the start page, set under Verwaltung › Module.
    owner: 'company',
    key: 'company.moduleOrder',
    level: 'company',
    type: 'arrangement',
    default: { order: [], hidden: [] },
  },
];

/** Role templates every new company starts with, in the same shape modules declare them. */
export const KERNEL_DEFAULT_ROLES: readonly (DefaultRole & { owner: string })[] = [
  {
    owner: 'company',
    key: 'company-admin',
    name: 'company.roles.company-admin',
    grants: [{ permission: 'company.*', scope: null }],
  },
  {
    owner: 'company',
    key: 'auditor',
    name: 'company.roles.auditor',
    grants: [{ permission: 'company.audit.read', scope: null }],
  },
  {
    // Name and work email are visible to every employee (ADR-0038).
    owner: 'core',
    key: 'people',
    name: 'core.roles.people',
    everyone: true,
    grants: [{ permission: 'core.people.read', scope: null }],
  },
];

/** Templates of the kernel and the given modules. The template key is `<owner>:<key>`. */
export function buildRoleTemplates(
  modules: readonly ModuleManifest[],
): (DefaultRole & { owner: string; templateKey: string })[] {
  return [
    ...KERNEL_DEFAULT_ROLES,
    ...modules.flatMap((m) => (m.defaultRoles ?? []).map((role) => ({ ...role, owner: m.id }))),
  ].map((role) => ({ ...role, templateKey: `${role.owner}:${role.key}` }));
}

/**
 * Personal data the kernel processes. Retention periods are proposals until the data protection
 * officer confirms them (docs/open-questions.md).
 */
export const KERNEL_DATA_PROCESSING: readonly DataProcessingDeclaration[] = [
  {
    entity: 'core.account',
    purpose:
      'Anmeldung per SSO oder Passwort, Zuordnung von Rechten, Anzeige im Profil und im Personenverzeichnis der Firma (ADR-0038)',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['sso-subject', 'name', 'email', 'last-login'],
    retention: { period: 'P90D', after: 'account-locked' },
    erase: 'anonymize',
  },
  {
    entity: 'core.login',
    purpose:
      'Anmeldung mit E-Mail-Adresse, Passwort, Authenticator-App und Passkey für Personen ohne SSO (ADR-0041, ADR-0044)',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: [
      'login-email',
      'password-hash',
      'authenticator-secret',
      'backup-codes',
      'passkey-public-key',
    ],
    retention: { period: 'P0D', after: 'account-erased' },
    erase: 'delete',
  },
  {
    entity: 'core.session',
    purpose: 'Sitzungsverwaltung und Widerruf von Anmeldungen',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'session-timestamps'],
    retention: { period: 'P30D', after: 'session-end' },
    erase: 'delete',
  },
  {
    entity: 'core.audit',
    purpose: 'Nachvollziehbarkeit von Änderungen und Zugriffen (ISO 27001 A.8.15)',
    legalBasis: 'Art. 6 Abs. 1 lit. c und f DSGVO',
    categories: ['account-id', 'action', 'timestamp'],
    retention: { period: 'P2Y', after: 'event' },
    erase: 'delete',
  },
  {
    entity: 'core.setting',
    purpose: 'Persönliche Einstellungen wie Sprache und Darstellung',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'preferences'],
    retention: { period: 'P0D', after: 'account-erased' },
    erase: 'delete',
  },
  {
    entity: 'core.saved-view',
    purpose: 'Gespeicherte Filter von Listen, persönlich oder für die ganze Firma',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'filter-values'],
    retention: { period: 'P0D', after: 'account-erased' },
    erase: 'delete',
  },
  {
    entity: 'core.reminder',
    purpose: 'Wiedervorlagen an Datensätzen mit Erinnerung am fälligen Tag',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'due-date', 'note'],
    retention: { period: 'P0D', after: 'account-erased' },
    erase: 'delete',
  },
  {
    entity: 'core.notification',
    purpose: 'Benachrichtigungen innerhalb der App',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'notification-content'],
    retention: { period: 'P90D', after: 'read' },
    erase: 'delete',
  },
  {
    // Old rows of the former WhatsApp channel, until a later version drops the table (ADR-0059).
    entity: 'core.notification-contact',
    purpose: 'Mobilnummer aus dem entfallenen WhatsApp-Kanal, nur noch für Auskunft und Löschung',
    legalBasis: 'Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)',
    categories: ['account-id', 'mobile-number'],
    retention: { period: 'P0D', after: 'consent-withdrawn' },
    erase: 'delete',
  },
  {
    entity: 'core.push-subscription',
    purpose:
      'Hinweis auf neue Benachrichtigungen als Push-Nachricht auf Geräten, für die die Person ihn eingeschaltet hat (ADR-0058)',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'push-address', 'device-kind'],
    retention: { period: 'P0D', after: 'switched-off' },
    erase: 'delete',
  },
  {
    entity: 'core.notification-dispatch',
    purpose:
      'Abstand zwischen Hinweisen per E-Mail und Teams, Zeitpunkt der letzten Push-Nachricht',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'timestamp'],
    retention: { period: 'P0D', after: 'account-erased' },
    erase: 'delete',
  },
  {
    entity: 'core.calendar-invite',
    purpose:
      'Kalendereinladungen per E-Mail: welche Person welchen Termin in welcher Fassung bekommen hat, damit Änderungen und Absagen nachkommen (ADR-0057)',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'record-ids', 'timestamp'],
    retention: { period: 'P0D', after: 'account-erased' },
    erase: 'delete',
  },
  {
    entity: 'core.event',
    purpose: 'Zustellung von Ereignissen zwischen Modulen',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'record-ids'],
    retention: { period: 'P30D', after: 'event' },
    erase: 'delete',
  },
  {
    entity: 'core.idempotency-key',
    purpose: 'Einmalige Ausführung wiederholter Schreibaufrufe, etwa aus der Offline-Warteschlange',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'response'],
    retention: { period: 'P30D', after: 'request' },
    erase: 'delete',
  },
  {
    entity: 'core.ai-usage',
    purpose:
      'Nachweis, wer welche KI-Funktion mit welcher Datenstufe genutzt hat, und Kostenkontrolle (ADR-0005)',
    legalBasis: 'Art. 6 Abs. 1 lit. c und f DSGVO',
    categories: ['account-id', 'ai-function', 'timestamp'],
    retention: { period: 'P1Y', after: 'event' },
    erase: 'anonymize',
  },
  {
    entity: 'core.signature',
    purpose: 'Persönliche Unterschrift und Funktion unter Berichten und Protokollen der Module',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['account-id', 'signature-image', 'job-title'],
    retention: { period: 'P0D', after: 'account-erased' },
    erase: 'delete',
  },
  {
    entity: 'core.job',
    purpose: 'Hintergrundarbeit wie Versand von Benachrichtigungen und Löschläufe',
    legalBasis: 'Art. 88 DSGVO i. V. m. § 26 BDSG',
    categories: ['record-ids'],
    retention: { period: 'P30D', after: 'job-finished' },
    erase: 'delete',
  },
];

export function buildPermissionCatalog(modules: readonly ModuleManifest[]): CatalogPermission[] {
  return [...KERNEL_PERMISSIONS, ...modules.flatMap(modulePermissions)];
}

/** AI functions of the kernel and the given modules. */
export function buildAiCatalog(
  modules: readonly ModuleManifest[],
): (AiFunctionDeclaration & { owner: string })[] {
  return [
    ...KERNEL_AI_FUNCTIONS,
    ...modules.flatMap((m) => (m.ai ?? []).map((f) => ({ ...f, owner: m.id }))),
  ];
}

export function buildSettingsCatalog(modules: readonly ModuleManifest[]): CatalogSetting[] {
  return [...KERNEL_SETTINGS, ...modules.flatMap(moduleSettings)];
}
